import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { authApi } from '../../api/endpoints/auth';
import { useAuthStore } from '../../stores/authStore';
import { useLoginFlowStore } from '../../stores/loginFlowStore';
import { handleApiError } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { CHALLENGE_SESSION_REJECTED_CODE } from '../../lib/otp';
import { buildPasswordSchema } from '../../lib/passwordRules';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';
import { PasswordRulesChecklist } from './PasswordRulesChecklist';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface Props {
    /** Fallback de la URL (deprecado): la fuente de verdad es el store. */
    session?: string;
    /** Fallback de la URL. */
    email?: string;
    /** Fallback de la URL. */
    tenantId?: string;
}

/**
 * Pantalla de "definí tu contraseña" del challenge NEW_PASSWORD_REQUIRED (DEC-RBAC-055).
 *
 * Lee `session`/`email`/`tenant` del store (search params quedan como fallback deprecado).
 * "Atrás" vuelve al paso password preservando email/tenant; "Cancelar" limpia el flujo. NO lleva
 * botón "reenviar": NEW_PASSWORD no es un OTP de CUSTOM_AUTH (usa la session nativa de
 * verifyPassword, no hay código que reenviar — DEC-RBAC-054).
 */
export function NewPasswordForm({
    session: sessionFromUrl,
    email: emailFromUrl,
    tenantId: tenantIdFromUrl,
}: Props) {
    const { t } = useTranslation(['auth']);
    const navigate = useNavigate();
    const refresh = useAuthStore((s) => s.refresh);
    const { policy } = usePasswordPolicy();

    // Store = fuente de verdad; la URL queda como fallback (deep-links viejos).
    const storeSession = useLoginFlowStore((s) => s.session);
    const storeEmail = useLoginFlowStore((s) => s.email);
    const storeTenantId = useLoginFlowStore((s) => s.tenantId);
    const setChallenge = useLoginFlowStore((s) => s.setChallenge);
    const back = useLoginFlowStore((s) => s.back);
    const reset = useLoginFlowStore((s) => s.reset);

    const session = storeSession ?? sessionFromUrl ?? '';
    const email = storeEmail || emailFromUrl || '';
    const tenantId = storeTenantId ?? tenantIdFromUrl;

    // Schema dinámico desde la policy del backend (DEC-RBAC-052), con fallback si el endpoint está
    // caído. Mismo modelo que recuperación, cambio y self-register. Memo por policy: RHF no necesita
    // un schema nuevo en cada render (solo cuando la policy efectiva cambia).
    const schema = useMemo(
        () =>
            z
                .object({
                    newPassword: buildPasswordSchema(policy),
                    confirmPassword: z.string(),
                })
                .refine((d) => d.newPassword === d.confirmPassword, {
                    message: t('auth:newPassword.mismatch'),
                    path: ['confirmPassword'],
                }),
        [policy, t],
    );
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: { newPassword: '', confirmPassword: '' },
    });
    const newPassword = useWatch({ control: form.control, name: 'newPassword' });

    const mutation = useMutation({
        mutationFn: (values: z.infer<typeof schema>) =>
            // Contrato real de /auth/challenge: { email, session, challengeType, challengeResponses }.
            authApi.challenge({
                email,
                session,
                // Tenant del picker (multipool): el backend lo usa para resolver el pool correcto.
                tenantId,
                challengeType: 'NEW_PASSWORD_REQUIRED',
                challengeResponses: { NEW_PASSWORD: values.newPassword },
            }),
        onSuccess: async (resp) => {
            // Shape real: { ok: true, challenge?: {...} }. Sin challenge = el flow terminó con
            // tokens (cookies seteadas) → limpiamos el store, refrescamos auth y vamos al home; el
            // guard de MFA (mfaSetupRequired) redirige a la elección de 2º factor si no tiene.
            if (!resp.challenge) {
                reset();
                await refresh();
                await navigate({ to: '/' });
                return;
            }
            // Caso raro: Cognito devuelve otro challenge tras el password. El session/email/tenant
            // viajan por el store (DEC-RBAC-055), ya no por la URL.
            switch (resp.challenge.challengeType) {
                case 'SOFTWARE_TOKEN_MFA':
                    setChallenge({
                        session: resp.challenge.session,
                        challengeType: 'SOFTWARE_TOKEN_MFA',
                    });
                    await navigate({ to: '/login/mfa-challenge' });
                    break;
                case 'EMAIL_OTP':
                    setChallenge({
                        session: resp.challenge.session,
                        challengeType: 'EMAIL_OTP',
                    });
                    await navigate({ to: '/login/mfa-challenge' });
                    break;
                default:
                    reset();
                    await refresh();
                    await navigate({ to: '/' });
            }
        },
        onError: (err) => {
            // La session de Cognito para fijar la contraseña venció: se vuelve a iniciar sesión.
            if (err instanceof ApiError && err.code === CHALLENGE_SESSION_REJECTED_CODE) {
                toast.error(t('auth:newPassword.sessionExpired'));
                back();
                void navigate({ to: '/login' });
                return;
            }
            handleApiError(err);
        },
    });

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit((v) => {
                    mutation.mutate(v);
                })(e);
            }}
            className="space-y-4"
            noValidate
        >
            <h1 className="text-2xl font-semibold text-center">{t('auth:newPassword.title')}</h1>
            <div className="space-y-2">
                <Label htmlFor="newPassword">{t('auth:newPassword.newPassword')}</Label>
                <Input
                    id="newPassword"
                    type="password"
                    {...form.register('newPassword')}
                    aria-invalid={!!form.formState.errors.newPassword}
                />
                <PasswordRulesChecklist value={newPassword} policy={policy} />
                {form.formState.errors.newPassword && (
                    <p className="text-sm text-destructive">
                        {form.formState.errors.newPassword.message}
                    </p>
                )}
            </div>
            <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth:newPassword.confirmPassword')}</Label>
                <Input
                    id="confirmPassword"
                    type="password"
                    {...form.register('confirmPassword')}
                    aria-invalid={!!form.formState.errors.confirmPassword}
                />
                {form.formState.errors.confirmPassword && (
                    <p className="text-sm text-destructive">
                        {form.formState.errors.confirmPassword.message}
                    </p>
                )}
            </div>
            <Button type="submit" className="w-full" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="animate-spin" />}
                {t('auth:newPassword.submit')}
            </Button>
            {/* "Atrás" vuelve al paso password preservando email/tenant (no a login vacío);
                "Cancelar" descarta el flujo. NO hay "reenviar": NEW_PASSWORD no es un OTP. */}
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => {
                        back();
                        void navigate({ to: '/login' });
                    }}
                    className="inline-flex items-center text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                    <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden />
                    Volver
                </button>
                <button
                    type="button"
                    onClick={() => {
                        reset();
                        void navigate({ to: '/login' });
                    }}
                    className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                    Cancelar
                </button>
            </div>
        </form>
    );
}
